<template>
  <section class="page-section">
    <div class="editor-head">
      <div class="editor-title-block">
        <h2 class="section-title">{{ pageTitle }}</h2>
        <div class="status-row">
          <el-tag :type="REPORT_STATUS_TYPE_MAP[form.status]">
            {{ REPORT_STATUS_TEXT_MAP[form.status] }}
          </el-tag>
          <el-tag v-if="form.source_document" type="success" effect="plain">已关联 Word</el-tag>
          <span class="muted">{{ form.updated_at || form.submitted_at || '尚未保存' }}</span>
        </div>
      </div>

      <div class="editor-head-actions">
        <el-button @click="router.push('/reports')">返回周报列表</el-button>
        <el-button plain @click="previewVisible = true">预览</el-button>
        <el-button :loading="saving" @click="handleSave(0)">保存草稿</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave(1)">提交审核</el-button>
      </div>
    </div>

    <p v-if="form.review_comment" class="review-note">审核反馈：{{ form.review_comment }}</p>

    <div class="mode-actions">
      <el-button :type="editMode === 'word' ? 'primary' : 'default'" @click="editMode = 'word'">
        <el-icon><UploadFilled /></el-icon>
        上传 Word
      </el-button>
      <el-button :type="editMode === 'editor' ? 'primary' : 'default'" @click="editMode = 'editor'">
        <el-icon><EditPen /></el-icon>
        在线编辑
      </el-button>
    </div>

    <div v-if="editMode === 'word'" class="upload-band">
      <el-upload
        class="upload-dropzone"
        drag
        :show-file-list="false"
        accept=".doc,.docx"
        :http-request="handleWordImport"
        :before-upload="beforeWordUpload"
        :disabled="importingWord"
      >
        <el-icon class="upload-icon"><UploadFilled /></el-icon>
        <div class="upload-title">{{ importingWord ? '正在导入…' : '拖拽或点击上传 Word 周报' }}</div>
        <div class="upload-subtitle">支持 .doc 和 .docx</div>
      </el-upload>

      <div v-if="form.source_document" class="source-doc">
        <div class="source-main">
          <el-tag type="success">Word</el-tag>
          <a
            :href="form.source_document.file_url"
            target="_blank"
            rel="noreferrer"
            class="source-link"
          >
            {{ form.source_document.file_name }}
          </a>
        </div>
        <div class="source-meta">
          <span class="muted">{{ formatFileSize(form.source_document.file_size) }}</span>
          <span class="muted">{{ form.source_document.uploaded_at }}</span>
        </div>
        <div class="source-actions">
          <el-button link @click="handleOpenSourceDocument">查看原件</el-button>
          <el-button link type="danger" :loading="removingSourceDocument" @click="clearSourceDocument">
            移除原件
          </el-button>
        </div>
      </div>
    </div>

    <el-form ref="formRef" :model="form" :rules="rules" label-width="88px" class="report-form">
      <el-form-item label="标题" prop="title">
        <el-input v-model="form.title" maxlength="200" show-word-limit placeholder="输入本周周报标题" />
      </el-form-item>

      <el-form-item label="周次" prop="report_week">
        <el-input v-model="form.report_week" maxlength="8" placeholder="2026-W29" />
      </el-form-item>

      <el-form-item label="正文" prop="content">
        <WangEditor v-model="form.content" :placeholder="editorPlaceholder" />
      </el-form-item>

      <el-form-item>
        <div class="footer-actions">
          <el-button :loading="saving" @click="handleSave(0)">保存草稿</el-button>
          <el-button type="primary" :loading="saving" @click="handleSave(1)">提交审核</el-button>
        </div>
      </el-form-item>
    </el-form>

    <el-dialog v-model="previewVisible" title="周报预览" width="min(860px, calc(100vw - 32px))">
      <div class="preview">
        <h3>{{ form.title || '未命名周报' }}</h3>
        <div class="preview-meta">
          <el-tag :type="REPORT_STATUS_TYPE_MAP[form.status]">
            {{ REPORT_STATUS_TEXT_MAP[form.status] }}
          </el-tag>
          <el-tag v-if="form.source_document" type="success" effect="plain">Word 原件</el-tag>
          <span class="muted">{{ form.updated_at || form.submitted_at || '尚未保存' }}</span>
        </div>

        <div v-if="form.source_document" class="source-doc preview-source">
          <div class="source-main">
            <el-tag type="success">Word</el-tag>
            <a
              :href="form.source_document.file_url"
              target="_blank"
              rel="noreferrer"
              class="source-link"
            >
              {{ form.source_document.file_name }}
            </a>
          </div>
          <div class="source-meta">
            <span class="muted">{{ formatFileSize(form.source_document.file_size) }}</span>
            <span class="muted">{{ form.source_document.uploaded_at }}</span>
          </div>
        </div>

        <div class="preview-body" v-html="sanitizedPreviewContent"></div>
      </div>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { EditPen, UploadFilled } from '@element-plus/icons-vue';
import { useRoute, useRouter } from 'vue-router';

import WangEditor from '../../components/WangEditor.vue';
import { deleteMyReportSourceDocument, getMyReportDetail, saveMyReport } from '../../api/report';
import { deleteWordDocument, importWordDocument } from '../../api/upload';
import { REPORT_STATUS_TEXT_MAP, REPORT_STATUS_TYPE_MAP } from '../../constants/report';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const EMPTY_CONTENT_HTML = new Set(['', '<p></p>', '<p><br></p>']);
const WORD_EXTENSIONS = ['.doc', '.docx'];
const MAX_WORD_FILE_SIZE = 20 * 1024 * 1024;

const route = useRoute();
const router = useRouter();

const formRef = ref();
const saving = ref(false);
const importingWord = ref(false);
const removingSourceDocument = ref(false);
const previewVisible = ref(false);
const editMode = ref(route.query.mode === 'word' ? 'word' : 'editor');

const form = reactive({
  id: '',
  title: '',
  report_week: buildWeekCode(),
  status: 0,
  cover_image: '',
  summary: '',
  content: '<p></p>',
  report_year: new Date().getFullYear(),
  report_month: new Date().getMonth() + 1,
  submitted_at: '',
  updated_at: '',
  review_comment: '',
  content_source: 'editor',
  source_document: null
});

const rules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  report_week: [
    { required: true, message: '请输入周次', trigger: 'blur' },
    {
      pattern: /^\d{4}-W\d{2}$/,
      message: '周次格式示例：2026-W29',
      trigger: 'blur'
    }
  ],
  content: [
    {
      validator: (rule, value, callback) => {
        if (!hasContent(value)) {
          callback(new Error('请输入正文'));
          return;
        }
        callback();
      },
      trigger: 'change'
    }
  ]
};

const pageTitle = computed(() => (route.params.id ? '编辑周报' : '新建周报'));
const editorPlaceholder = computed(() =>
  editMode.value === 'word' ? 'Word 导入后，可以继续补充和微调正文' : '输入本周周报正文'
);
const sanitizedPreviewContent = computed(() => sanitizeHtml(form.content));

function buildWeekCode() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), 0, 1);
  const passedDays = Math.floor((now - firstDay) / 86400000);
  const week = Math.ceil((passedDays + firstDay.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function parseHtml(html) {
  if (typeof DOMParser === 'undefined') return null;
  return new DOMParser().parseFromString(html || '', 'text/html');
}

function normalizeReportWeek(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
}

function hasContent(html) {
  const normalized = String(html || '').trim();
  if (EMPTY_CONTENT_HTML.has(normalized)) return false;

  const doc = parseHtml(normalized);
  if (!doc) return !EMPTY_CONTENT_HTML.has(normalized);

  const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  return Boolean(text || doc.querySelector('img, video, iframe'));
}

function buildSummary(html, title) {
  const doc = parseHtml(html);
  if (!doc) return String(title || '').trim().slice(0, 120);

  const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 120);
  if (doc.querySelector('video, iframe')) return '视频内容';
  if (doc.querySelector('img')) return '图片内容';
  return String(title || '').trim().slice(0, 120);
}

function buildCover(html) {
  const doc = parseHtml(html);
  return doc?.querySelector('img')?.getAttribute('src') || '';
}

function formatFileSize(size) {
  const value = Number(size) || 0;
  if (!value) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function syncDerivedFields() {
  form.report_week = normalizeReportWeek(form.report_week);
  form.summary = buildSummary(form.content, form.title);
  form.cover_image = buildCover(form.content);
  form.content_source = form.source_document ? 'word_import' : 'editor';

  const [yearPart] = form.report_week.split('-W');
  if (yearPart && /^\d{4}$/.test(yearPart)) {
    form.report_year = Number(yearPart);
  }
}

function beforeWordUpload(file) {
  const fileName = String(file.name || '').toLowerCase();
  const isWord = WORD_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  if (!isWord) {
    ElMessage.warning('请上传 .doc 或 .docx 文件');
    return false;
  }

  if ((file.size || 0) > MAX_WORD_FILE_SIZE) {
    ElMessage.warning('Word 文件请控制在 20MB 以内');
    return false;
  }

  return true;
}

async function handleWordImport({ file, onSuccess, onError }) {
  importingWord.value = true;

  try {
    const previousTempSourceDocument = !form.id ? form.source_document : null;
    const result = await importWordDocument(file);
    const imported = result.data || {};

    if (imported.title) {
      form.title = imported.title;
    }

    if (typeof imported.content === 'string' && imported.content.trim()) {
      form.content = imported.content;
    }

    form.source_document = imported.source_document || null;
    form.content_source = imported.content_source || (form.source_document ? 'word_import' : 'editor');

    if (
      previousTempSourceDocument?.file_url &&
      previousTempSourceDocument.file_url !== form.source_document?.file_url
    ) {
      try {
        await deleteWordDocument(previousTempSourceDocument);
      } catch (cleanupError) {
        console.warn('Failed to cleanup previous temp word file.', cleanupError);
      }
    }

    ElMessage.success(result.message || 'Word 导入成功');
    onSuccess?.(result);
  } catch (error) {
    ElMessage.error(error.response?.data?.message || 'Word 导入失败');
    onError?.(error);
  } finally {
    importingWord.value = false;
  }
}

function handleOpenSourceDocument() {
  if (!form.source_document?.file_url) return;
  window.open(form.source_document.file_url, '_blank', 'noopener,noreferrer');
}

async function clearSourceDocument() {
  if (!form.source_document) return;

  try {
    await ElMessageBox.confirm('移除后将不再保留这份 Word 原件，确定继续吗？', '删除确认', {
      type: 'warning'
    });
  } catch (error) {
    return;
  }

  removingSourceDocument.value = true;

  try {
    if (form.id) {
      const result = await deleteMyReportSourceDocument(form.id);
      Object.assign(form, result.data, {
        content_source: result.data?.content_source || 'editor'
      });
    } else {
      await deleteWordDocument(form.source_document);
      form.source_document = null;
      form.content_source = 'editor';
    }

    ElMessage.success('Word 原件已移除');
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '移除原件失败');
  } finally {
    removingSourceDocument.value = false;
  }
}

const loadDetail = async () => {
  if (!route.params.id) return;
  try {
    const result = await getMyReportDetail(route.params.id);
    Object.assign(form, result.data, {
      content_source: result.data?.content_source || (result.data?.source_document ? 'word_import' : 'editor')
    });
    if (form.source_document) {
      editMode.value = 'word';
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取周报详情失败');
  }
};

const handleSave = async (status) => {
  if (!formRef.value) return;

  syncDerivedFields();

  try {
    await formRef.value.validate();
  } catch (error) {
    return;
  }

  saving.value = true;

  try {
    const payload = {
      ...form,
      status
    };
    const result = await saveMyReport(payload);
    const saved = result.data;
    Object.assign(form, saved, {
      content_source: saved?.content_source || (saved?.source_document ? 'word_import' : 'editor')
    });

    if (!route.params.id && saved.id) {
      const query = editMode.value === 'word' ? { mode: 'word' } : {};
      await router.replace({ path: `/reports/${saved.id}`, query });
    }

    ElMessage.success(status === 1 ? '提交成功' : '保存成功');
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '保存失败');
  } finally {
    saving.value = false;
  }
};

onMounted(loadDetail);
</script>

<style scoped>
.editor-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.editor-title-block {
  min-width: 0;
}

.editor-head-actions,
.mode-actions,
.status-row,
.source-main,
.source-meta,
.source-actions,
.preview-meta,
.footer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.review-note {
  margin: 0 0 16px;
  color: #4b5563;
  line-height: 1.7;
}

.mode-actions {
  margin-bottom: 16px;
}

.upload-band {
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
}

.upload-dropzone :deep(.el-upload-dragger) {
  width: 100%;
  border-radius: 8px;
}

.upload-icon {
  font-size: 28px;
  color: #2563eb;
}

.upload-title {
  margin-top: 10px;
  color: #111827;
  font-weight: 600;
}

.upload-subtitle {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.source-doc {
  display: grid;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.source-link {
  color: #2563eb;
  word-break: break-word;
}

.report-form {
  margin-top: 4px;
}

.footer-actions {
  width: 100%;
}

.preview h3 {
  margin: 0;
  font-size: 28px;
  word-break: break-word;
}

.preview-meta {
  margin: 12px 0 20px;
}

.preview-source {
  margin-bottom: 20px;
}

.preview-body {
  line-height: 1.9;
  word-break: break-word;
  overflow: hidden;
}

@media (max-width: 768px) {
  .editor-head-actions,
  .footer-actions {
    width: 100%;
  }
}
</style>
